import { ShieldExclamationIcon } from '@heroicons/react/24/outline'
import { humanToolName } from '../../lib/messageContent'
import ChoicePrompt from './ChoicePrompt'
import { useChat } from '../../providers/chat/context'

const CHIP = 'rounded-md bg-stone-200 px-1.5 py-px text-[10px] font-medium text-stone-600'

function creditsLabel(credits: number) {
  if (credits <= 0) return null
  return credits < 1 ? '< 1 credit' : `~${Math.round(credits)} credit${credits >= 2 ? 's' : ''}`
}

function ordinal(count: number) {
  const tens = count % 100
  if (tens >= 11 && tens <= 13) return `${count}th`
  return `${count}${['th', 'st', 'nd', 'rd'][count % 10] ?? 'th'}`
}

export default function PermissionPrompt() {
  const { pendingPermission, permissionRepeat, busy, decide } = useChat()
  if (!pendingPermission) return null

  const credits = creditsLabel(pendingPermission.estimated_cost_credits)
  const disabled = busy === 'deciding'
  const again = permissionRepeat > 0

  return (
    <ChoicePrompt
      icon={<ShieldExclamationIcon className="size-3.5 text-purple-700" />}
      title={<span className="capitalize">{humanToolName(pendingPermission.tool_name)}</span>}
      description={
        again
          ? `Asked again: this is the ${ordinal(permissionRepeat + 1)} call. Allow once covers one call.`
          : 'This needs your approval before it runs.'
      }
      actions={[
        { label: 'Deny', variant: 'muted', disabled, onClick: () => void decide('deny') },
        { label: 'Always allow', variant: 'outline', disabled, onClick: () => void decide('always') },
        ...(pendingPermission.in_plan
          ? [{ label: 'Allow for this plan', variant: 'outline' as const, disabled, onClick: () => void decide('plan') }]
          : []),
        { label: 'Allow once', variant: 'primary', disabled, onClick: () => void decide('once') },
      ]}
    >
      {pendingPermission.detail && (
        <p className="mt-1.5 line-clamp-2 rounded-md bg-white px-2 py-1 text-[11px] text-stone-700">
          {pendingPermission.detail}
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap gap-1">
        <span className={`${CHIP} capitalize`}>{pendingPermission.action}</span>
        {credits && <span className={CHIP}>{credits}</span>}
        {again && <span className={CHIP}>Call {permissionRepeat + 1}</span>}
      </div>
    </ChoicePrompt>
  )
}
